/**
 * Servicio de invocación del AI Copilot.
 * Usa streaming SSE para mostrar respuestas en tiempo real.
 */

import { supabase } from '@/lib/supabase';
import type { AICopilotRequest, AICopilotStreamCallback } from '@/types/ai.types';

export type AICopilotStatusCallback = (status: 'connected' | 'thinking') => void;

/**
 * Invoca al AI Copilot con streaming.
 * Llama onChunk con cada token conforme llega, y al final con {done: true, response, thread_id}.
 */
export async function invokeCopilotStream(
  request: AICopilotRequest,
  onChunk: AICopilotStreamCallback,
  onStatus?: AICopilotStatusCallback,
): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('No hay sesión activa');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const res = await fetch(`${supabaseUrl}/functions/v1/ai-copilot`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: supabaseAnonKey,
    },
    body: JSON.stringify(request),
  });

  if (!res.ok) {
    const errText = await res.text();
    let errMsg = `Error ${res.status}`;
    try { errMsg = JSON.parse(errText).error ?? errMsg; } catch { /* */ }
    throw new Error(errMsg);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6).trim();
      if (!payload) continue;

      try {
        const parsed = JSON.parse(payload);
        if (parsed.status && onStatus) {
          onStatus(parsed.status);
        } else {
          onChunk(parsed);
        }
      } catch {
        // skip unparseable
      }
    }
  }

  // Process remaining buffer
  if (buffer.startsWith('data: ')) {
    try {
      const parsed = JSON.parse(buffer.slice(6).trim());
      onChunk(parsed);
    } catch { /* */ }
  }
}

/**
 * Solicita transcripción de una llamada.
 */
export async function requestCallTranscription(callId: string): Promise<{
  success: boolean;
  transcription?: string;
  summary?: string;
  sentiment?: string;
  key_topics?: string[];
  action_items?: Array<{ task: string; assignee_suggestion: string }>;
  error?: string;
}> {
  const { data: { session } } = await supabase.auth.getSession();
  const { data, error } = await supabase.functions.invoke('ai-call-transcription', {
    body: { call_id: callId },
    headers: session?.access_token
      ? { Authorization: `Bearer ${session.access_token}` }
      : undefined,
  });

  if (error) {
    throw new Error(error.message || 'Error al transcribir llamada');
  }

  return data;
}
